import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ExternalLink, Mail, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * SubscribeModal
 *
 * In-site dialog that opens when any "Subscribe" CTA is clicked (navbar,
 * footer, etc.). Users stay on jclawsc.org — the modal explains what
 * they're signing up for and has a "Continue to Subscribe" button that
 * takes them to the JC Law Squarespace signup page in the same tab.
 *
 * Why a modal + navigate (and not a true inline form)?
 *   The Squarespace signup page cannot be embedded (X-Frame-Options blocks
 *   iframes) and its API cannot be submitted from external sites (domain-
 *   bound reCAPTCHA Enterprise). This modal is the closest UX to inline
 *   signup: the visual funnel lives on jclawsc.org and Squarespace only
 *   handles the actual signup step.
 */
const SUBSCRIBE_URL = "https://lilac-owl-xzbg.squarespace.com/signup";

const SubscribeModal = ({ isOpen, onClose, triggerRef }) => {
    const primaryBtnRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const previouslyFocused = document.activeElement;
        document.body.style.overflow = "hidden";

        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);

        const timer = setTimeout(() => primaryBtnRef.current?.focus(), 50);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKey);
            clearTimeout(timer);
            const target = triggerRef?.current || previouslyFocused;
            if (target && typeof target.focus === "function") {
                target.focus();
            }
        };
    }, [isOpen, onClose, triggerRef]);

    const continueToSubscribe = () => {
        // Same-tab navigation keeps the flow feeling like one experience.
        window.location.href = SUBSCRIBE_URL;
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4 md:px-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="subscribe-modal-title"
                    data-testid="subscribe-modal"
                >
                    <motion.button
                        type="button"
                        aria-label="Close dialog"
                        onClick={onClose}
                        className="absolute inset-0 bg-navy-950/85 backdrop-blur-sm cursor-default"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="subscribe-modal-backdrop"
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="relative bg-editorial text-navy-900 shadow-2xl w-full max-w-lg md:max-w-xl overflow-hidden"
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            data-testid="subscribe-modal-close"
                            className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full text-navy-900/70 hover:text-navy-900 hover:bg-navy-900/5 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        {/* Navy header */}
                        <div className="bg-navy-900 text-editorial px-6 md:px-8 py-6 relative overflow-hidden">
                            <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
                            <div className="relative flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-coral">
                                    <Mail className="w-5 h-5 text-white" />
                                </span>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold">Stay Connected</p>
                                    <h2 id="subscribe-modal-title" className="font-serif text-2xl md:text-3xl leading-tight">
                                        Subscribe to Updates
                                    </h2>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 md:px-8 py-6 md:py-8">
                            <p className="text-navy-800/85 leading-relaxed">
                                Join the JC Law Foundation email list to receive updates on the <strong>Hotel Cardiac Emergency Preparedness Act</strong>, petition milestones, and ways to help save lives.
                            </p>

                            <ul className="mt-5 space-y-2.5 text-sm text-navy-800/85">
                                <li className="flex items-start gap-2.5">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" aria-hidden />
                                    <span>Advocacy alerts and legislative progress</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" aria-hidden />
                                    <span>Petition milestones and calls to action</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" aria-hidden />
                                    <span>Foundation stories, events, and resources</span>
                                </li>
                            </ul>

                            <div className="mt-6 flex items-start gap-2.5 text-xs text-navy-700/70 leading-relaxed">
                                <ShieldCheck className="w-4 h-4 mt-0.5 text-coral flex-shrink-0" aria-hidden />
                                <p>
                                    By subscribing, you agree to receive updates from JC Law Foundation. You can unsubscribe at any time. See our{" "}
                                    <Link
                                        to="/privacy-policy"
                                        onClick={onClose}
                                        className="underline underline-offset-2 hover:text-coral"
                                    >
                                        Privacy Policy
                                    </Link>
                                    .
                                </p>
                            </div>

                            <div className="mt-7 flex flex-col sm:flex-row gap-3">
                                <button
                                    ref={primaryBtnRef}
                                    type="button"
                                    onClick={continueToSubscribe}
                                    data-testid="subscribe-modal-continue"
                                    className="group inline-flex items-center justify-center gap-2 flex-1 px-6 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors"
                                >
                                    Continue to Subscribe
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    data-testid="subscribe-modal-cancel"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-navy-900/20 text-navy-900 hover:border-navy-900 hover:bg-navy-900/5 transition-colors"
                                >
                                    Not Now
                                </button>
                            </div>

                            <p className="mt-4 text-xs text-navy-700/60 flex items-center gap-1.5">
                                <ExternalLink className="w-3 h-3" aria-hidden />
                                <span>Signup is handled by our newsletter partner.</span>
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SubscribeModal;
